import React, { useEffect, useRef } from 'react';
import { SOULS, SOUL_IDS, hueVars } from './soul';
import { mountSoulForms } from './soulForms';
import { hapticTick } from '../../../_utils/haptics';

/**
 * The four soul cards, as Ben designed them: dark cards with a living
 * sculpted form each, in the soul's colour. Choosing one floods the card
 * with its colour, shows a tick and grows the form.
 *
 * `value` is the chosen soul id (or null once the dials move away from all
 * four). `motion` false holds the forms still (the tab's Pause motion).
 */
export function useSoulForms(ref, on = true) {
    useEffect(() => {
        if (!on || !ref.current) return undefined;
        let forms = null;
        try { forms = mountSoulForms(ref.current); } catch (e) { forms = null; }
        return () => { if (forms) forms.destroy(); };
    }, [ref, on]);
}

export default function SoulCards({ value, onPick, motion = true, forms = true }) {
    const root = useRef(null);
    useSoulForms(root, forms);

    const pick = (id, el) => {
        hapticTick();
        if (el) { el.classList.remove('is-pop'); void el.offsetWidth; el.classList.add('is-pop'); } // eslint-disable-line no-void
        onPick(id);
    };
    const onKey = (e) => {
        if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(e.key)) return;
        e.preventDefault();
        const at = Math.max(0, SOUL_IDS.indexOf(value));
        const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1;
        const next = SOUL_IDS[(at + step + SOUL_IDS.length) % SOUL_IDS.length];
        onPick(next);
        const el = root.current && root.current.querySelector(`[data-soul="${next}"]`);
        if (el) el.focus();
    };

    return (
        <div className="soul-cards" ref={root} data-motion={motion ? 'true' : 'false'} role="group" aria-label="Choose Bailey’s soul" onKeyDown={onKey}>
            {SOULS.map((p) => (
                <button
                    key={p.id}
                    type="button"
                    className="soul-card"
                    data-soul={p.id}
                    aria-pressed={value === p.id}
                    style={hueVars(p.hue)}
                    onClick={(e) => pick(p.id, e.currentTarget)}
                >
                    <span className="soul-glow" aria-hidden="true" />
                    <span className="soul-symbol" aria-hidden="true" />
                    <span className="soul-check" aria-hidden="true"><span>✓</span></span>
                    <span className="soul-name">{p.name}</span>
                    <span className="soul-desc">{p.desc}</span>
                </button>
            ))}
        </div>
    );
}

/** A small still of a soul's form, for tiles and rows. */
export function SoulMark({ hue, size = 22 }) {
    return <span className="soul-mark" style={{ ...hueVars(hue), width: size, height: Math.round(size * 0.92) }} aria-hidden="true" />;
}
